/**
 * Created by Alican Köker on 2019-05-14.
 */
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {IReelStopSpinData, ReelController} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {SpinReelController} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinReelController";
import {EdisonHelper} from "../../utils/EdisonHelper";
import {EdisonGameConfig} from "../EdisonGameConfig";
import {EdisonRandomFeatureController} from "../features/randomFeature/EdisonRandomFeatureController";
import {EdisonGameMode} from "../gameMode/EdisonGameMode";
import {EdisonParsedGameData} from "../server/data/EdisonParsedGameData";
import {EdisonStateReelSymbol} from "./reelSymbol/EdisonStateReelSymbol";

export class EdisonReelController extends SpinReelController {

    private _gameData:EdisonParsedGameData;
    private _responseTime:number = 0;
    private _startTime:number = 0;
    private _randomFeatureTriggered:boolean;
    private _nearFreespinReels:boolean[];

    constructor(reelId:number) {
        super(reelId);
    }

    protected addEventListeners():void {
        super.addEventListeners();
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, (event:GameEvent) => this.onBeforeSpinStart());
        EventHandler.addEventListener(this, EdisonRandomFeatureController.RANDOM_FEATURE_TRIGGERED, (event:GameEvent) => this._randomFeatureTriggered = event.params[0]);
    }

    protected onBeforeSpinStart() {
        this._startTime = this.getStartTime();
    }

    private getStartTime():number {
        return new Date().getTime();
    };

    public onGameDataParsed(data:EdisonParsedGameData):void {
        this._responseTime = this.calculateTimeDiff(this._startTime);

        super.onGameDataParsed(data);
        this._gameData = data;
        if(data.stackedNearWinReels.indexOf(true) > -1) {
            this._nearWinReels = data.stackedNearWinReels;
        }

        this._nearFreespinReels = EdisonHelper.calculateNearFreespinReels(this._gameData.evaluatedArea);
    }

    private calculateTimeDiff(startTime:number):number {
        let endTime:number = new Date().getTime();
        let timeDiff:number = endTime - startTime; //in ms
        let coeff:number = 1000;
        return timeDiff / coeff;
    }

    protected setCurrentReelSet(data:{ reelSetName:string, reelSetPosition?:number }):void {
        if(this._gameData.randomM1WildModeTriggeredThisSpin || this._gameData.randomM2WildModeTriggeredThisSpin) {
            data.reelSetName = this._gameData.randomM1WildModeTriggeredThisSpin ? "RANDOM_M1" : "RANDOM_M2";
        }

        super.setCurrentReelSet(data);
    }

    protected createNextSymbol(previousSymbol?:StateReelSymbol | null, init?:boolean):StateReelSymbol {
        let sym:EdisonStateReelSymbol = <EdisonStateReelSymbol>super.createNextSymbol(previousSymbol, init);

        if(this._gameData) {
            let goldSym:boolean = (sym.symName === "M1" && this._gameData!.randomM1WildModeTriggeredThisSpin) ||
                (sym.symName === "M2" && this._gameData!.randomM2WildModeTriggeredThisSpin);
            if(goldSym) {
                sym.changeState({
                    state : EdisonGameConfig.instance.SYMBOL_STATES.gold,
                    fadeInDuration : 0,
                    fadeInDelay : 0,
                });
            }
        }

        return sym;
    }

    protected getStopDelayDelta(stopSpinData:IReelStopSpinData):number {
        const randomFeatureIntroAnimationDuration:number = 7.5;
        const respinFeatureIntroAnimationDuration:number = 4;

        let diffMultiplier:number = this._responseTime === 3 ? 1 : this._responseTime > 3 ? 0 : (3 - this._responseTime) / 3;

        let delta:number = super.getStopDelayDelta(stopSpinData);

        if(this.reelId === 0 && this._gameData!.mode === EdisonGameMode.STACKED_RESPIN)
            delta += respinFeatureIntroAnimationDuration;

        if(this.reelId === 0 && this._randomFeatureTriggered)
            delta += randomFeatureIntroAnimationDuration;

        if(this._gameData && this._gameData.mode === EdisonGameMode.FREESPIN) {
            //if(this.checkChangesOnReel(reelId))
            if(stopSpinData.fastSpin)
                return EdisonGameConfig.instance.REEL_QUICK_STOP_DELAY_FREESPIN + delta;
            else if(stopSpinData.quickStop)
                return EdisonGameConfig.instance.REEL_STOP_FAST_SPIN_DELAY_FREESPIN + delta;
            else
                return EdisonGameConfig.instance.REEL_STOP_DELAY_FREESPIN + delta;
        }

        if(this._gameData && this._gameData!.nearWinReels[this.reelId]) {
            delta += EdisonGameConfig.instance.REEL_NEAR_WIN_STOP_DELAY;
        }

        return delta * diffMultiplier;
    }

    protected getQuickStopDelay(reelId:number):number {
        let firstSNWR:number = this.getFirstStackedReel();
        if(this._gameData.stackedNearWinReels[reelId] && reelId !== firstSNWR)
            return EdisonGameConfig.instance.REEL_NEAR_WIN_STOP_DELAY_RESPIN[reelId];
        else
            return ReelController._reelQuickStopDelays[reelId];
    }

    protected getNearWinStopDelay(reelId:number):number {
        if(this._gameData.stackedNearWinReels[reelId] || this._nearFreespinReels[reelId]) {
            return EdisonGameConfig.instance.REEL_NEAR_WIN_STOP_DELAY_RESPIN[reelId];
        } else
            return ReelController._reelNearWinStopDelays[reelId];
    }

    private getFirstStackedReel():number {
        for(let i:number = 0; i < EdisonGameConfig.instance.REELS_NUM; i++) {
            if(this._gameData.stackedNearWinReels[i])
                return i - 1;
        }

        return -1;
    }

    public hasNearWin(reelId?:number):boolean {
        reelId = reelId ?? this.reelId;
        return (this._nearWinReels != null && this._nearWinReels[reelId]) || (this._nearFreespinReels != null && this._nearFreespinReels[reelId]);
    }
}